/**
 * Page admin : Suivi des chauffeurs en temps réel sur carte Leaflet.
 */
import { useEffect, useMemo, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Tooltip } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { RefreshCw, MapPin, Battery, Navigation, Clock } from 'lucide-react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import ReglagesSuiviDialog from '@/components/ReglagesSuiviDialog';

// Fix Leaflet default icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const iconActif = new L.DivIcon({
  className: 'custom-marker-actif',
  html: `<div style="background:#22c55e;width:18px;height:18px;border-radius:50%;border:3px solid white;box-shadow:0 0 8px rgba(34,197,94,0.7);"></div>`,
  iconSize: [18, 18],
  iconAnchor: [9, 9],
});
const iconInactif = new L.DivIcon({
  className: 'custom-marker-inactif',
  html: `<div style="background:#9ca3af;width:14px;height:14px;border-radius:50%;border:2px solid white;"></div>`,
  iconSize: [14, 14],
  iconAnchor: [7, 7],
});

export default function SuiviChauffeursPage() {
  const [chauffeurs, setChauffeurs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [delai, setDelai] = useState(15);
  const { user } = useAuth();
  const estAdmin = ['owner', 'admin', 'dirigeant', 'super_admin'].includes(user?.role);

  const fetchData = async () => {
    try {
      const res = await api.get('/geoloc/chauffeurs-actifs');
      setChauffeurs(res.data?.chauffeurs || []);
      if (res.data?.delai_inactif_min) setDelai(res.data.delai_inactif_min);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    if (!autoRefresh) return;
    const id = setInterval(fetchData, 30000); // refresh toutes les 30s
    return () => clearInterval(id);
  }, [autoRefresh]);

  const center = useMemo(() => {
    if (chauffeurs.length === 0) return [44.0, 5.0]; // Sud-Est France par défaut
    const lats = chauffeurs.map(c => c.lat).filter(Number.isFinite);
    const lons = chauffeurs.map(c => c.lon).filter(Number.isFinite);
    if (!lats.length) return [44.0, 5.0];
    return [
      lats.reduce((a, b) => a + b, 0) / lats.length,
      lons.reduce((a, b) => a + b, 0) / lons.length,
    ];
  }, [chauffeurs]);

  const actifs = chauffeurs.filter(c => c.is_active);
  const inactifs = chauffeurs.filter(c => !c.is_active);

  return (
    <div className="space-y-4" data-testid="suivi-chauffeurs-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Navigation className="w-7 h-7 text-primary" />
            Suivi chauffeurs en temps réel
          </h1>
          <p className="text-muted-foreground">
            Position GPS envoyée quand l'appli est ouverte, selon vos réglages (consentement RGPD requis)
          </p>
        </div>
        <div className="flex gap-2">
          {estAdmin && <ReglagesSuiviDialog onEnregistre={fetchData} />}
          <Button variant={autoRefresh ? 'default' : 'outline'} onClick={() => setAutoRefresh(v => !v)}>
            {autoRefresh ? 'Auto-refresh ON' : 'Auto-refresh OFF'}
          </Button>
          <Button variant="outline" onClick={fetchData} data-testid="btn-refresh-chauffeurs">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
          <CardContent className="p-4">
            <p className="text-3xl font-bold">{actifs.length}</p>
            <p className="text-xs" data-testid="libelle-actifs">Actifs (&lt;{delai} min)</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-gray-500 to-gray-600 text-white">
          <CardContent className="p-4">
            <p className="text-3xl font-bold">{inactifs.length}</p>
            <p className="text-xs">Inactifs</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-3xl font-bold">{chauffeurs.length}</p>
            <p className="text-xs text-muted-foreground">Total chauffeurs</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-muted-foreground" />
            <div>
              <p className="text-xs text-muted-foreground">Refresh</p>
              <p className="font-medium text-sm">30 sec</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <MapPin className="w-4 h-4" /> Carte des positions
          </CardTitle>
          <CardDescription>Vert = actif, Gris = inactif</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-[500px] rounded-lg overflow-hidden border" data-testid="map-chauffeurs">
            <MapContainer center={center} zoom={9} style={{ height: '100%', width: '100%' }}>
              <TileLayer
                attribution='&copy; OpenStreetMap'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {chauffeurs.map((c) => (
                Number.isFinite(c.lat) && Number.isFinite(c.lon) && (
                  <Marker
                    key={c.user_id}
                    position={[c.lat, c.lon]}
                    icon={c.is_active ? iconActif : iconInactif}
                  >
                    <Tooltip direction="top" offset={[0, -8]}>
                      {c.user_name || c.user_id?.slice(0, 8)} · {c.minutes_ago != null ? `${c.minutes_ago} min` : '?'}
                    </Tooltip>
                    <Popup>
                      <div className="text-sm space-y-1">
                        <p className="font-bold">{c.user_name || 'Chauffeur'}</p>
                        <p>Vitesse : {c.speed != null ? `${Math.round(c.speed)} km/h` : '—'}</p>
                        <p>Batterie : {c.battery != null ? `${c.battery}%` : '—'}</p>
                        <p className="text-xs text-muted-foreground">
                          Dernier ping : {c.minutes_ago != null ? `il y a ${c.minutes_ago} min` : '—'}
                        </p>
                        <p className="text-xs">{c.lat.toFixed(5)}, {c.lon.toFixed(5)}</p>
                      </div>
                    </Popup>
                  </Marker>
                )
              ))}
            </MapContainer>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Liste des chauffeurs</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-center py-6 text-muted-foreground">Chargement...</p>
          ) : chauffeurs.length === 0 ? (
            <p className="text-center py-6 text-muted-foreground">
              Aucun chauffeur n'a partagé sa position pour le moment.
            </p>
          ) : (
            <div className="divide-y" data-testid="liste-chauffeurs">
              {chauffeurs.map((c) => (
                <div key={c.user_id} className="py-3 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full ${c.is_active ? 'bg-green-500' : 'bg-gray-400'}`} />
                    <div>
                      <p className="font-medium">{c.user_name || c.user_id?.slice(0, 8)}</p>
                      <p className="text-xs text-muted-foreground">
                        {c.minutes_ago != null ? `il y a ${c.minutes_ago} min` : 'jamais'}
                        {c.speed != null && ` · ${Math.round(c.speed)} km/h`}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2 items-center">
                    {c.battery != null && (
                      <Badge variant="outline" className="gap-1">
                        <Battery className="w-3 h-3" /> {c.battery}%
                      </Badge>
                    )}
                    <Badge variant={c.is_active ? 'default' : 'secondary'}>
                      {c.is_active ? 'Actif' : 'Inactif'}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
